import { isOrHandleAsExternalUrl } from "../../../helpers";
import { useAnimateFadeIn, useAnimateFadeInToTop } from "../../../animation/animation";
import type { TIndexableBlock, TLink } from "../../../types";
import Carousel from "../../elements/carousel/carousel";
import Heading from "../../elements/heading/heading";
import styles from "./card-list-block-v2.module.less";
import { m as motion } from "motion/react";
import { Card, TCardProduct } from "../../elements/card/card";
import { LinkAnchor } from "../../elements";
import { isArrayWithItems } from "../../../helpers/array-helper";

export type TCardListBlockProps = {
  standardCards?: Array<any>;
  productCards?: Array<TCardProduct>;
  title?: string;
  text?: string;
  link?: TLink;
  isCarousel?: boolean;
} & TIndexableBlock;

export const CardListBlockV2 = ({
  title,
  text,
  link,
  standardCards,
  productCards,
  isCarousel,
  index: blockIndex,
}: TCardListBlockProps) => {
  const fadeInAnimation = useAnimateFadeIn({ whileInView: true, easing: "easeInOut", displayAmountTreshold: 0.01 });
  const fadeInAnimationCarousel = useAnimateFadeIn({
    whileInView: true,
    easing: "easeInOut",
  });
  const fadeInToTopAnimation = useAnimateFadeInToTop({ whileInView: true, delay: 0.2, easing: "easeOut" });

  const mappedStandardCards = standardCards
    ? standardCards.map((card) => {
        return <Card.Standard {...card} />;
      })
    : [];

  const mappedProductCards = productCards ? productCards.map((card) => <Card.Product {...card} />) : [];

  return (
    <section className={styles.wrapper}>
      <motion.div
        {...(isCarousel ? fadeInAnimationCarousel : fadeInAnimation)}
        className={[styles.innerWrapper, "max-width padded-sides"].join(" ")}
      >
        {(title || link) && (
          <div className={styles.topWrapper}>
            {title && (
              <Heading
                index={blockIndex}
                semanticType={"h2"}
                visualType={"h3"}
                className={[styles.title, "heading-max-width"].join(" ")}
              >
                {title}
              </Heading>
            )}
            {link && (
              <motion.div {...fadeInToTopAnimation} className={styles.linkWrapper}>
                {
                  <LinkAnchor.Secondary
                    label={link.title}
                    url={link.url}
                    iconRight={link.icon ?? "ui-icon-arrow-right"}
                    openInNewTab={isOrHandleAsExternalUrl(link.url, link.target)}
                    onPreNavigate={link.onPreNavigate}
                  />
                }
              </motion.div>
            )}
          </div>
        )}
        {text && <p className={[styles.text, "body-l text-max-width"].join(" ")}>{text}</p>}

        {isCarousel ? (
          <Carousel
            wrapperClassName={styles.carouselWrapper}
            items={
              isArrayWithItems(mappedProductCards)
                ? mappedProductCards
                : isArrayWithItems(mappedStandardCards)
                  ? mappedStandardCards
                  : []
            }
          />
        ) : (
          <ul className={`${styles.list}`}>
            {isArrayWithItems(mappedProductCards) &&
              mappedProductCards?.map((card, index) => {
                return (
                  <li key={index} className={styles.item}>
                    {card}
                  </li>
                );
              })}
            {isArrayWithItems(mappedStandardCards) &&
              mappedStandardCards?.map((card, index) => {
                return (
                  <li key={index} className={styles.item}>
                    {card}
                  </li>
                );
              })}
          </ul>
        )}
      </motion.div>
    </section>
  );
};

export default CardListBlockV2;
